/**
 * FirebaseTeamMessagingService - Internal team messaging between practice members
 *
 * Firestore collection: team_conversations/{id}/messages/{id}
 * Supports: 1:1 direct messages, group conversations, practice-wide channels
 */
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  writeBatch,
  limit,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// ============ TYPES ============

export interface TeamConversation {
  id: string;
  name: string;
  type: 'direct' | 'group';
  members: string[];                      // Array of UIDs
  memberNames: Record<string, string>;    // uid → display name
  memberRoles: Record<string, string>;    // uid → role
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  lastMessage?: {
    content: string;
    senderId: string;
    senderName: string;
    timestamp: Date;
  };
  unreadCount: Record<string, number>;    // uid → unread count
  isArchived: boolean;
}

export interface TeamMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  content: string;
  timestamp: Date;
  read: boolean;
  readBy: string[];     // UIDs who have read this message
  isEdited: boolean;
  editedAt?: Date;
  isDeleted: boolean;
  deletedAt?: Date;
}

export type TeamMessagesCallback = (messages: TeamMessage[]) => void;
export type TeamConversationsCallback = (conversations: TeamConversation[]) => void;
export type UnsubscribeFunction = () => void;

// ============ SERVICE ============

class FirebaseTeamMessagingService {
  private static instance: FirebaseTeamMessagingService;
  private collectionName = 'team_conversations';
  private messagesSubcollection = 'messages';
  private typingCollection = 'teamTypingIndicators';

  static getInstance(): FirebaseTeamMessagingService {
    if (!FirebaseTeamMessagingService.instance) {
      FirebaseTeamMessagingService.instance = new FirebaseTeamMessagingService();
    }
    return FirebaseTeamMessagingService.instance;
  }

  // ============ CONVERSATION METHODS ============

  /**
   * Create a new team conversation (direct or group)
   */
  async createConversation(
    members: Array<{ uid: string; name: string; role: string }>,
    name: string,
    createdBy: string,
    type: 'direct' | 'group' = 'direct'
  ): Promise<string> {
    try {
      // For direct messages, check if 1:1 conversation already exists
      if (type === 'direct' && members.length === 2) {
        const existing = await this.findDirectConversation(members[0].uid, members[1].uid);
        if (existing) return existing.id;
      }

      const memberUids = members.map(m => m.uid);
      const memberNames: Record<string, string> = {};
      const memberRoles: Record<string, string> = {};
      const unreadCount: Record<string, number> = {};

      members.forEach(m => {
        memberNames[m.uid] = m.name;
        memberRoles[m.uid] = m.role;
        unreadCount[m.uid] = 0;
      });

      const conversationData = {
        name,
        type,
        members: memberUids,
        memberNames,
        memberRoles,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: null,
        unreadCount,
        isArchived: false
      };

      const docRef = await addDoc(collection(db, this.collectionName), conversationData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating team conversation:', error);
      throw error;
    }
  }

  /**
   * Find existing direct conversation between two users
   */
  async findDirectConversation(uid1: string, uid2: string): Promise<TeamConversation | null> {
    try {
      // Query where both UIDs are in members array
      const q = query(
        collection(db, this.collectionName),
        where('type', '==', 'direct'),
        where('members', 'array-contains', uid1)
      );

      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data.members.includes(uid2) && data.members.length === 2) {
          return this.convertToConversation(docSnap.id, data);
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding direct conversation:', error);
      return null;
    }
  }

  /**
   * Subscribe to team conversations for a user
   */
  subscribeToConversations(
    userId: string,
    callback: TeamConversationsCallback
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.collectionName),
      where('members', 'array-contains', userId),
      where('isArchived', '==', false),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const conversations = snapshot.docs.map(doc =>
        this.convertToConversation(doc.id, doc.data())
      );
      callback(conversations);
    }, (error) => {
      console.error('Error subscribing to team conversations:', error);
      callback([]);
    });
  }

  /**
   * Get a conversation by ID
   */
  async getConversation(conversationId: string): Promise<TeamConversation | null> {
    try {
      const docRef = doc(db, this.collectionName, conversationId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;
      return this.convertToConversation(docSnap.id, docSnap.data());
    } catch (error) {
      console.error('Error getting team conversation:', error);
      return null;
    }
  }

  /**
   * Add members to a group conversation
   */
  async addMembers(
    conversationId: string,
    newMembers: Array<{ uid: string; name: string; role: string }>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, conversationId);
      const updates: Record<string, any> = {
        members: arrayUnion(...newMembers.map(m => m.uid)),
        updatedAt: serverTimestamp()
      };
      newMembers.forEach(m => {
        updates[`memberNames.${m.uid}`] = m.name;
        updates[`memberRoles.${m.uid}`] = m.role;
        updates[`unreadCount.${m.uid}`] = 0;
      });
      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error adding members:', error);
      throw error;
    }
  }

  /**
   * Remove a member from a group conversation
   */
  async removeMember(conversationId: string, uid: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, conversationId);
      await updateDoc(docRef, {
        members: arrayRemove(uid),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  }

  // ============ MESSAGE METHODS ============

  /**
   * Send a message to a team conversation
   */
  async sendMessage(
    conversationId: string,
    senderId: string,
    senderName: string,
    senderRole: string,
    content: string
  ): Promise<string> {
    try {
      const messageData = {
        conversationId,
        senderId,
        senderName,
        senderRole,
        content,
        timestamp: serverTimestamp(),
        read: false,
        readBy: [senderId],
        isEdited: false,
        editedAt: null,
        isDeleted: false,
        deletedAt: null
      };

      const messagesRef = collection(
        db, this.collectionName, conversationId, this.messagesSubcollection
      );
      const docRef = await addDoc(messagesRef, messageData);

      // Get conversation to update unread counts for all OTHER members
      const convoRef = doc(db, this.collectionName, conversationId);
      const convoSnap = await getDoc(convoRef);
      if (convoSnap.exists()) {
        const convoData = convoSnap.data();
        const updates: Record<string, any> = {
          lastMessage: {
            content,
            senderId,
            senderName,
            timestamp: serverTimestamp()
          },
          updatedAt: serverTimestamp()
        };

        // Increment unread for every member except sender
        (convoData.members || []).forEach((uid: string) => {
          if (uid !== senderId) {
            const current = convoData.unreadCount?.[uid] || 0;
            updates[`unreadCount.${uid}`] = current + 1;
          }
        });

        await updateDoc(convoRef, updates);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error sending team message:', error);
      throw error;
    }
  }

  /**
   * Subscribe to messages in a conversation
   */
  subscribeToMessages(
    conversationId: string,
    callback: TeamMessagesCallback
  ): UnsubscribeFunction {
    const messagesRef = collection(
      db, this.collectionName, conversationId, this.messagesSubcollection
    );
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc =>
        this.convertToMessage(doc.id, doc.data())
      );
      callback(messages);
    }, (error) => {
      if ((error as any)?.code === 'permission-denied') {
        console.warn('Team messages: permission denied for', conversationId);
      } else {
        console.error('Error subscribing to team messages:', error);
      }
      callback([]);
    });
  }

  /**
   * Mark messages as read for a user
   */
  async markAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      const messagesRef = collection(
        db, this.collectionName, conversationId, this.messagesSubcollection
      );

      // Get messages not read by this user
      const q = query(messagesRef, where('read', '==', false));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // Still reset unread count
        const convoRef = doc(db, this.collectionName, conversationId);
        await updateDoc(convoRef, { [`unreadCount.${userId}`]: 0 });
        return;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (!data.readBy?.includes(userId)) {
          batch.update(docSnap.ref, {
            readBy: arrayUnion(userId)
          });
        }
      });

      // Reset unread count for this user
      const convoRef = doc(db, this.collectionName, conversationId);
      batch.update(convoRef, { [`unreadCount.${userId}`]: 0 });

      await batch.commit();
    } catch (error) {
      console.error('Error marking team messages as read:', error);
    }
  }

  /**
   * Edit a message
   */
  async editMessage(conversationId: string, messageId: string, newContent: string): Promise<void> {
    try {
      const messageRef = doc(
        db, this.collectionName, conversationId, this.messagesSubcollection, messageId
      );
      await updateDoc(messageRef, {
        content: newContent,
        isEdited: true,
        editedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error editing team message:', error);
      throw error;
    }
  }

  /**
   * Soft-delete a message
   */
  async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    try {
      const messageRef = doc(
        db, this.collectionName, conversationId, this.messagesSubcollection, messageId
      );
      await updateDoc(messageRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        content: '[Message deleted]'
      });
    } catch (error) {
      console.error('Error deleting team message:', error);
      throw error;
    }
  }

  // ============ TYPING INDICATOR ============

  async setTyping(conversationId: string, userId: string, userName: string, isTyping: boolean): Promise<void> {
    try {
      const typingRef = doc(db, this.typingCollection, `${conversationId}_${userId}`);
      if (isTyping) {
        await setDoc(typingRef, {
          conversationId, userId, userName, isTyping: true, timestamp: serverTimestamp()
        });
      } else {
        await deleteDoc(typingRef).catch(() => {});
      }
    } catch (error) {
      console.error('Error setting team typing indicator:', error);
    }
  }

  subscribeToTyping(
    conversationId: string,
    currentUserId: string,
    callback: (typingUsers: Array<{ userId: string; userName: string }>) => void
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.typingCollection),
      where('conversationId', '==', conversationId)
    );

    return onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const typingUsers = snapshot.docs
        .map(d => d.data())
        .filter(t => {
          if (t.userId === currentUserId || !t.isTyping) return false;
          const ts = (t.timestamp as any)?.toDate?.() || t.timestamp;
          return ts && (now - new Date(ts).getTime()) < 15000;
        })
        .map(t => ({ userId: t.userId, userName: t.userName }));
      callback(typingUsers);
    }, () => callback([]));
  }

  // ============ UNREAD COUNT ============

  subscribeToUnreadCount(
    userId: string,
    callback: (count: number) => void
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.collectionName),
      where('members', 'array-contains', userId),
      where('isArchived', '==', false)
    );

    return onSnapshot(q, (snapshot) => {
      let total = 0;
      snapshot.docs.forEach(doc => {
        total += doc.data().unreadCount?.[userId] || 0;
      });
      callback(total);
    }, () => callback(0));
  }

  // ============ ARCHIVE ============

  async archiveConversation(conversationId: string): Promise<void> {
    const docRef = doc(db, this.collectionName, conversationId);
    await updateDoc(docRef, { isArchived: true, updatedAt: serverTimestamp() });
  }

  // ============ HELPERS ============

  private convertToConversation(id: string, data: any): TeamConversation {
    return {
      id,
      name: data.name || '',
      type: data.type || 'direct',
      members: data.members || [],
      memberNames: data.memberNames || {},
      memberRoles: data.memberRoles || {},
      createdBy: data.createdBy || '',
      createdAt: this.toDate(data.createdAt),
      updatedAt: this.toDate(data.updatedAt),
      lastMessage: data.lastMessage ? {
        content: data.lastMessage.content,
        senderId: data.lastMessage.senderId,
        senderName: data.lastMessage.senderName,
        timestamp: this.toDate(data.lastMessage.timestamp)
      } : undefined,
      unreadCount: data.unreadCount || {},
      isArchived: data.isArchived || false
    };
  }

  private convertToMessage(id: string, data: any): TeamMessage {
    return {
      id,
      conversationId: data.conversationId,
      senderId: data.senderId,
      senderName: data.senderName,
      senderRole: data.senderRole || '',
      content: data.content,
      timestamp: this.toDate(data.timestamp),
      read: data.read || false,
      readBy: data.readBy || [],
      isEdited: data.isEdited || false,
      editedAt: data.editedAt ? this.toDate(data.editedAt) : undefined,
      isDeleted: data.isDeleted || false,
      deletedAt: data.deletedAt ? this.toDate(data.deletedAt) : undefined
    };
  }

  private toDate(ts: Timestamp | Date | null): Date {
    if (!ts) return new Date();
    if (ts instanceof Timestamp) return ts.toDate();
    return ts;
  }
}

export const firebaseTeamMessagingService = FirebaseTeamMessagingService.getInstance();
